/**
 * ========================================
 * TABLE KEY HELPERS - REACT KEY GENERATION
 * ========================================
 * 
 * Utility functions to generate unique keys for React table components
 * This helps avoid React warnings about missing or non-unique keys
 * 
 * @author Your Team
 * @version 1.0.0
 * @since 2024
 */

/**
 * Generate a unique key for table rows
 * @param {Object} rowData - The data object for the row
 * @param {number} index - The index of the row in the array
 * @param {string} prefix - Optional prefix for the key (default: 'row')
 * @returns {string} Unique key for the table row
 */
export const generateRowKey = (rowData, index, prefix = 'row') => {
  /**
   * Helper function to extract the actual value from potentially nested objects
   * Handles cases like { id: 123 } or { id: { data: 123 } }
   */
  const extractValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'object') {
      // Handle nested .data property
      if (value.data !== undefined && value.data !== null) {
        return value.data;
      }
      // Handle nested .value property
      if (value.value !== undefined && value.value !== null) {
        return value.value;
      }
      // If it's still an object, return null to avoid [object Object]
      return null;
    }
    return value;
  };
  
  // Try to use common unique identifiers first
  const idValue = extractValue(rowData?.id);
  if (idValue) return `${prefix}-${idValue}`;
  
  const uuidValue = extractValue(rowData?.uuid);
  if (uuidValue) return `${prefix}-${uuidValue}`;
  
  const _idValue = extractValue(rowData?._id);
  if (_idValue) return `${prefix}-${_idValue}`;
  
  // Try to find any field that looks like an ID
  const idFields = Object.keys(rowData).filter(key => 
    key.toLowerCase().includes('id') && rowData[key]
  );
  if (idFields.length > 0) {
    const fieldValue = extractValue(rowData[idFields[0]]);
    if (fieldValue) {
      return `${prefix}-${fieldValue}`;
    }
  }
  
  // Fallback to index only (safer than trying to stringify complex objects)
  return `${prefix}-${index}`;
};

/**
 * Generate a unique key for table headers
 * @param {Object|string} header - The header object or string
 * @param {number} index - The index of the header in the array
 * @param {string} prefix - Optional prefix for the key (default: 'header')
 * @returns {string} Unique key for the table header
 */
export const generateHeaderKey = (header, index, prefix = 'header') => {
  if (typeof header === 'object') {
    if (header?.key) return `${prefix}-${header.key}`;
    if (header?.label) return `${prefix}-${header.label}`;
    if (header?.id) return `${prefix}-${header.id}`;
  }
  
  if (typeof header === 'string') {
    return `${prefix}-${header.toLowerCase().replace(/\s+/g, '-')}`;
  }
  
  return `${prefix}-${index}`;
};

/**
 * Generate a unique key for table cells
 * @param {Object} rowData - The data object for the row
 * @param {number} rowIndex - The index of the row
 * @param {string} cellKey - The key of the cell data
 * @param {number} cellIndex - The index of the cell in the row
 * @param {string} prefix - Optional prefix for the key (default: 'cell')
 * @returns {string} Unique key for the table cell
 */
export const generateCellKey = (rowData, rowIndex, cellKey, cellIndex, prefix = 'cell') => {
  const rowKey = generateRowKey(rowData, rowIndex, 'row');
  return `${rowKey}-${prefix}-${cellKey || cellIndex}`;
};

/**
 * Generate a unique key for action buttons
 * @param {Object} rowData - The data object for the row
 * @param {number} rowIndex - The index of the row
 * @param {string} actionType - The type of action
 * @param {number} actionIndex - The index of the action
 * @returns {string} Unique key for the action button
 */
export const generateActionKey = (rowData, rowIndex, actionType, actionIndex) => {
  const rowKey = generateRowKey(rowData, rowIndex, 'row');
  return `${rowKey}-action-${actionType}-${actionIndex}`;
};

/**
 * Check if a key is valid for React
 * @param {string} key - The key to validate
 * @returns {boolean} True if the key is valid
 */
export const isValidReactKey = (key) => {
  return typeof key === 'string' && key.length > 0 && !key.includes(' ');
};

/**
 * Generate a fallback key when no unique identifier is available
 * @param {string} prefix - The prefix for the key
 * @param {number} index - The index
 * @param {string} suffix - Optional suffix
 * @returns {string} Fallback key
 */
export const generateFallbackKey = (prefix, index, suffix = '') => {
  return `${prefix}-${index}${suffix ? `-${suffix}` : ''}`;
};
